import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Chip,
  Divider,
  Button,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Fab,
  IconButton,
  Tooltip,
  Collapse,
  Stack,
  useTheme,
  useMediaQuery,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Avatar,
  alpha,
} from '@mui/material';
import {
  CreditCard,
  AttachMoney,
  Warning,
  CheckCircle,
  FilterList,
  Cancel,
  ExpandMore,
  ExpandLess,
  Clear,
  CalendarMonth,
  Person,
  TrendingUp,
  AccountBalance
} from '@mui/icons-material';
import SysDateRangePicker from '../components/SysDateRangePicker';
import { format, startOfMonth, endOfMonth, subMonths, startOfYear, endOfYear, subDays } from 'date-fns';
import { ptBR } from 'date-fns/locale';

import clienteCreditoService, { ClienteCredito } from '../services/clienteCreditoService';
import CreditoForm from '../components/forms/CreditoForm';

interface KpiCardProps {
  titulo: string;
  valor: string | number;
  subtitulo?: string;
  icone: React.ReactNode;
  cor: string;
  bgCor: string;
}

const KpiCard: React.FC<KpiCardProps> = ({ titulo, valor, subtitulo, icone, cor, bgCor }) => (
  <Card elevation={0} sx={{ height: '100%', borderRadius: 2, border: (t) => `1px solid ${alpha(t.palette.divider, 0.8)}` }}>
    <CardContent sx={{ p: 1.25, '&:last-child': { pb: 1.25 } }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" gap={1}>
        <Box minWidth={0}>
          <Typography variant="caption" color="text.secondary" display="block" lineHeight={1.2}>{titulo}</Typography>
          <Typography variant="h6" fontWeight={800} color={cor} lineHeight={1.2}>{valor}</Typography>
          {subtitulo && <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>{subtitulo}</Typography>}
        </Box>
        <Avatar sx={{ bgcolor: bgCor, width: 34, height: 34, '& .MuiSvgIcon-root': { fontSize: 18 } }}>{icone}</Avatar>
      </Box>
    </CardContent>
  </Card>
);

// Função utilitária para obter o primeiro e último dia do mês atual
const getCurrentMonthRange = () => {
  const now = new Date();
  return {
    startDate: startOfMonth(now),
    endDate: endOfMonth(now)
  };
};

// Opções de filtros rápidos
const quickFilterOptions = [
  {
    label: 'Hoje',
    value: 'today',
    getRange: () => ({
      startDate: new Date(),
      endDate: new Date()
    })
  },
  {
    label: 'Mês Atual',
    value: 'current-month',
    getRange: () => getCurrentMonthRange()
  },
  {
    label: 'Últimos 30 dias',
    value: 'last-30',
    getRange: () => ({
      startDate: subDays(new Date(), 30),
      endDate: new Date()
    })
  },
  {
    label: 'Últimos 3 meses',
    value: 'last-3-months',
    getRange: () => ({
      startDate: subMonths(new Date(), 3),
      endDate: new Date()
    })
  },
  {
    label: 'Este Ano',
    value: 'current-year',
    getRange: () => ({
      startDate: startOfYear(new Date()),
      endDate: endOfYear(new Date())
    })
  }
];

interface CreditosFilters {
  tipo?: string;
  cliente?: string;
  status?: string;
  dataInicio?: Date | null;
  dataFim?: Date | null;
  nivel?: string;
}

const CreditosPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Estados
  const [filters, setFilters] = useState<CreditosFilters>({});
  const [showFilters, setShowFilters] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedQuickFilter, setSelectedQuickFilter] = useState<string>('current-month');
  const [showCreditoForm, setShowCreditoForm] = useState(false);

  // Aplicar filtro rápido inicial
  useEffect(() => {
    const quickFilter = quickFilterOptions.find(qf => qf.value === selectedQuickFilter);
    if (quickFilter) {
      const range = quickFilter.getRange();
      setFilters(prev => ({
        ...prev,
        dataInicio: range.startDate,
        dataFim: range.endDate
      }));
    }
  }, [selectedQuickFilter]);

  // Buscar créditos
  const { data: creditosData, isLoading, error, refetch } = useQuery({
    queryKey: ['creditos', filters, page, rowsPerPage, searchTerm],
    queryFn: () => clienteCreditoService.getAllCreditos(filters, page, rowsPerPage, searchTerm),
    placeholderData: (previousData) => previousData
  });

  // Estatísticas
  const { data: estatisticas } = useQuery({
    queryKey: ['creditos-estatisticas'],
    queryFn: () => clienteCreditoService.getEstatisticasGerais(),
    refetchInterval: 300000 // 5 minutos
  });

  // Handlers
  const handleFilterChange = (field: keyof CreditosFilters, value: any) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPage(0);
  };

  const handleQuickFilterChange = (filterValue: string) => {
    setSelectedQuickFilter(filterValue);
    setPage(0);
  };

  const handleClearFilters = () => {
    setFilters({});
    setSearchTerm('');
    setSelectedQuickFilter('current-month');
    setPage(0);
  };

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const handlePageChange = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleCreditoSuccess = () => {
    refetch();
    // Mostrar mensagem de sucesso se necessário
  };

  const handleCreditoError = (error: string) => {
    console.error('Erro ao criar crédito:', error);
    // Mostrar mensagem de erro se necessário
  };

  // Formatação
  const formatarMoeda = (valor: number): string => {
    return valor.toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  const formatDate = (date: string | Date) => {
    return format(new Date(date), 'dd/MM/yyyy', { locale: ptBR });
  };

  const getTipoColor = (tipo: string) => {
    switch (tipo) {
      case 'cadastro': return 'primary';
      case 'recorrente': return 'success';
      case 'bonificacao': return 'info';
      default: return 'default';
    }
  };

  const getStatusColor = (utilizado: boolean, saldo: number) => {
    if (utilizado || saldo === 0) return 'error';
    if (saldo > 0) return 'success';
    return 'default';
  };

  const getStatusText = (utilizado: boolean, saldo: number) => {
    if (utilizado || saldo === 0) return 'Utilizado';
    if (saldo > 0) return 'Disponível';
    return 'Indefinido';
  };

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">
          Erro ao carregar créditos: {error.message}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1, md: 1.5 }, mx: 'auto' }}>
      <Paper
        elevation={0}
        sx={{
          mb: 1.5,
          p: { xs: 1.25, md: 1.5 },
          borderRadius: 2,
          background: theme.palette.mode === 'dark'
            ? `linear-gradient(135deg, ${alpha(theme.palette.warning.dark, 0.35)} 0%, ${alpha(theme.palette.primary.main, 0.25)} 50%, ${alpha('#1a1a2e', 0.9)} 100%)`
            : `linear-gradient(135deg, ${alpha('#fff8e1', 0.95)} 0%, ${alpha(theme.palette.primary.light, 0.15)} 45%, ${alpha(theme.palette.primary.main, 0.08)} 100%)`,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        <Box sx={{ position: 'absolute', top: -20, right: -10, opacity: 0.08, transform: 'rotate(15deg)' }}>
          <CreditCard sx={{ fontSize: 120, color: theme.palette.warning.main }} />
        </Box>
        <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between" spacing={1.25}>
          <Stack direction="row" alignItems="center" spacing={1.25}>
            <Avatar
              sx={{
                width: 40,
                height: 40,
                background: `linear-gradient(135deg, ${theme.palette.warning.main} 0%, ${theme.palette.warning.dark} 100%)`,
                boxShadow: `0 4px 12px ${alpha(theme.palette.warning.main, 0.35)}`,
              }}
            >
              <CreditCard sx={{ fontSize: 22 }} />
            </Avatar>
            <Box flex={1} minWidth={0}>
              <Typography variant="h6" fontWeight={800} lineHeight={1.2}>Gestão de Créditos</Typography>
              <Typography variant="caption" color="text.secondary">Dashboard e gerenciamento de créditos</Typography>
            </Box>
          </Stack>
          {!isMobile && (
            <Button variant="contained" startIcon={<CreditCard />} onClick={() => setShowCreditoForm(true)} size="small">
              Novo Crédito
            </Button>
          )}
        </Stack>
      </Paper>

      {estatisticas && (
        <Grid container spacing={1} mb={1.5}>
          <Grid item xs={6} md={3}>
            <KpiCard titulo="Total Disponível" valor={`R$ ${formatarMoeda(estatisticas.totalDisponivel || 0)}`} icone={<AccountBalance sx={{ color: theme.palette.primary.main }} />} cor={theme.palette.primary.main} bgCor={alpha(theme.palette.primary.main, 0.12)} />
          </Grid>
          <Grid item xs={6} md={3}>
            <KpiCard titulo="Total Gerado" valor={`R$ ${formatarMoeda(estatisticas.totalGerado || 0)}`} icone={<TrendingUp sx={{ color: theme.palette.success.main }} />} cor={theme.palette.success.main} bgCor={alpha(theme.palette.success.main, 0.12)} />
          </Grid>
          <Grid item xs={6} md={3}>
            <KpiCard titulo="Créditos Ativos" valor={estatisticas.creditosAtivos || 0} icone={<CheckCircle sx={{ color: theme.palette.info.main }} />} cor={theme.palette.info.main} bgCor={alpha(theme.palette.info.main, 0.12)} />
          </Grid>
          <Grid item xs={6} md={3}>
            <KpiCard titulo="% Utilizado" valor={`${estatisticas.percentualUtilizado || '0.00'}%`} icone={<AttachMoney sx={{ color: theme.palette.warning.main }} />} cor={theme.palette.warning.main} bgCor={alpha(theme.palette.warning.main, 0.12)} />
          </Grid>
        </Grid>
      )}

      <Paper elevation={0} sx={{ p: { xs: 1.25, md: 1.5 }, mb: 1.5, borderRadius: 2, border: `1px solid ${theme.palette.divider}`, bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.04 : 0.02) }}>
        <Stack direction="row" alignItems="center" spacing={0.75} mb={1}>
          <CalendarMonth color="primary" sx={{ fontSize: 18 }} />
          <Typography variant="body2" fontWeight={700}>Período Rápido</Typography>
        </Stack>
        <Box display="flex" flexWrap="wrap" gap={1}>
          {quickFilterOptions.map((option) => (
            <Button
              key={option.value}
              variant={selectedQuickFilter === option.value ? 'contained' : 'outlined'}
              size="small"
              onClick={() => handleQuickFilterChange(option.value)}
              sx={{ borderRadius: 1.5, textTransform: 'none', fontWeight: 600, fontSize: '0.75rem' }}
            >
              {option.label}
            </Button>
          ))}
        </Box>
      </Paper>

      <Paper elevation={0} sx={{ p: { xs: 1.25, md: 1.5 }, mb: 1.5, borderRadius: 2, border: `1px solid ${theme.palette.divider}`, bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.04 : 0.02) }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={showFilters ? 1 : 0}>
          <Stack direction="row" alignItems="center" spacing={0.75}>
            <FilterList color="primary" sx={{ fontSize: 18 }} />
            <Typography variant="body2" fontWeight={700}>Filtros Avançados</Typography>
          </Stack>
          <Button size="small" startIcon={showFilters ? <ExpandLess /> : <ExpandMore />} onClick={() => setShowFilters(!showFilters)} sx={{ textTransform: 'none' }}>
            {showFilters ? 'Ocultar' : 'Mostrar'}
          </Button>
        </Box>

        <Collapse in={showFilters}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={12} md={3}>
              <SysDateRangePicker
                onChange={(range: { startDate: Date | null; endDate: Date | null }) => {
                  handleFilterChange('dataInicio', range.startDate || null);
                  handleFilterChange('dataFim', range.endDate || null);
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>Tipo</InputLabel>
                <Select
                  value={filters.tipo || ''}
                  onChange={(e) => handleFilterChange('tipo', e.target.value)}
                  label="Tipo"
                  size="small"
                >
                  <MenuItem value="">Todos</MenuItem>
                  <MenuItem value="bonificacao">Bonificação</MenuItem>
                  <MenuItem value="cadastro">Cadastro</MenuItem>
                  <MenuItem value="recorrente">Recorrente</MenuItem>
                  <MenuItem value="pagamento_excedente">Pagamento Excedente</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.status || ''}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  label="Status"
                  size="small"
                >
                  <MenuItem value="">Todos</MenuItem>
                  <MenuItem value="disponivel">Disponível</MenuItem>
                  <MenuItem value="utilizado">Utilizado</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>Nível</InputLabel>
                <Select
                  value={filters.nivel || ''}
                  onChange={(e) => handleFilterChange('nivel', e.target.value)}
                  label="Nível"
                  size="small"
                >
                  <MenuItem value="">Todos</MenuItem>
                  <MenuItem value="1">Nível 1</MenuItem>
                  <MenuItem value="2">Nível 2</MenuItem>
                  <MenuItem value="3">Nível 3</MenuItem>
                  <MenuItem value="4">Nível 4</MenuItem>
                  <MenuItem value="5">Nível 5</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={10}>
              <TextField
                fullWidth
                label="Buscar por cliente"
                value={searchTerm}
                onChange={handleSearch}
                placeholder="Nome ou ID do cliente"
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Button
                variant="outlined"
                startIcon={<Clear />}
                onClick={handleClearFilters}
                fullWidth
              >
                Limpar Filtros
              </Button>
            </Grid>
          </Grid>
        </Collapse>
      </Paper>

      <Paper elevation={0} sx={{ width: '100%', overflow: 'hidden', borderRadius: 2, border: `1px solid ${theme.palette.divider}` }}>
        <Box sx={{ px: 1.5, py: 0.75, display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: alpha(theme.palette.primary.main, 0.04), borderBottom: `1px solid ${theme.palette.divider}` }}>
          <Typography variant="body2" fontWeight={700}>Listagem de Créditos</Typography>
          {!isLoading && creditosData && (
            <Chip size="small" label={`${creditosData.pagination?.total || 0} crédito${(creditosData.pagination?.total || 0) !== 1 ? 's' : ''}`} color="primary" variant="outlined" sx={{ height: 22, fontSize: '0.7rem' }} />
          )}
        </Box>
        <TableContainer sx={{ maxHeight: 520 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow sx={{
                bgcolor: alpha(theme.palette.primary.main, 0.06),
                '& .MuiTableCell-head': {
                  fontWeight: 700,
                  fontSize: '0.68rem',
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                  color: 'text.secondary',
                  py: 0.75,
                  px: 1,
                  borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.15)}`,
                },
              }}>
                <TableCell>Cliente</TableCell>
                <TableCell>Tipo</TableCell>
                <TableCell>Valor</TableCell>
                <TableCell>Saldo</TableCell>
                <TableCell>Cliente Origem</TableCell>
                <TableCell>Nível</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Data Criação</TableCell>
                <TableCell>Data Utilização</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 5 }}>
                    <CircularProgress size={32} />
                    <Typography variant="caption" color="text.secondary" display="block" mt={1}>Carregando créditos...</Typography>
                  </TableCell>
                </TableRow>
              ) : creditosData?.creditos?.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                    <CreditCard sx={{ fontSize: 40, color: alpha(theme.palette.text.secondary, 0.25), mb: 1 }} />
                    <Typography variant="subtitle2" fontWeight={600}>Nenhum crédito encontrado</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                creditosData?.creditos?.map((credito: ClienteCredito) => (
                  <TableRow key={credito.id} hover sx={{ '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.03) } }}>
                    <TableCell sx={{ py: 0.5, fontSize: '0.78rem' }}>
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {credito.cliente?.nome || `Cliente ${credito.cliente_id}`}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          ID: {credito.cliente_id}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ py: 0.5, fontSize: '0.78rem' }}>
                      <Chip
                        label={credito.tipo}
                        color={getTipoColor(credito.tipo)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell sx={{ py: 0.5, fontSize: '0.78rem' }}>
                      <Typography variant="body2" fontWeight="medium">
                        R$ {formatarMoeda(credito.valor)}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 0.5, fontSize: '0.78rem' }}>
                      <Typography variant="body2" fontWeight="medium">
                        R$ {formatarMoeda(credito.saldo)}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 0.5, fontSize: '0.78rem' }}>
                      {credito.cliente_origem && (
                        <Box>
                          <Typography variant="body2">
                            {credito.cliente_origem?.nome || `Cliente ${credito.cliente_origem_id}`}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            ID: {credito.cliente_origem_id}
                          </Typography>
                        </Box>
                      )}
                    </TableCell>
                    <TableCell sx={{ py: 0.5, fontSize: '0.78rem' }}>
                      {credito.nivel && (
                        <Chip
                          label={`Nível ${credito.nivel}`}
                          variant="outlined"
                          size="small"
                        />
                      )}
                    </TableCell>
                    <TableCell sx={{ py: 0.5, fontSize: '0.78rem' }}>
                      <Chip
                        label={getStatusText(credito.utilizado, credito.saldo)}
                        color={getStatusColor(credito.utilizado, credito.saldo)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell sx={{ py: 0.5, fontSize: '0.78rem' }}>
                      <Typography variant="body2">
                        {formatDate(credito.createdAt)}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 0.5, fontSize: '0.78rem' }}>
                      {credito.data_utilizacao ? (
                        <Typography variant="body2">
                          {formatDate(credito.data_utilizacao)}
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          -
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Paginação */}
        {creditosData && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              py: 1.5,
              px: 2,
              bgcolor: alpha(theme.palette.primary.main, 0.04),
              borderTop: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`,
            }}
          >
            <TablePagination
              rowsPerPageOptions={[10, 25, 50, 100]}
              component="div"
              count={creditosData.pagination?.total || 0}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleRowsPerPageChange}
              labelRowsPerPage="Linhas por página:"
              labelDisplayedRows={({ from, to, count }) =>
                `${from}-${to} de ${count !== -1 ? count : `mais de ${to}`}`
              }
              sx={{
                '.MuiTablePagination-toolbar': {
                  minHeight: 44,
                  flexWrap: 'wrap',
                  justifyContent: 'center',
                  gap: 2,
                  '& .MuiTablePagination-spacer': { display: 'none' },
                },
                '.MuiTablePagination-selectLabel': { m: 0, fontSize: '0.875rem' },
                '.MuiTablePagination-select': { py: 0.5, pr: 2.5, fontSize: '0.875rem' },
                '.MuiTablePagination-displayedRows': { m: 0, fontSize: '0.875rem' },
                '.MuiTablePagination-actions': { ml: 0.5 },
              }}
            />
          </Box>
        )}
      </Paper>

      {isMobile && (
        <Fab color="primary" aria-label="adicionar" sx={{ position: 'fixed', bottom: 86, right: 16 }} onClick={() => setShowCreditoForm(true)}>
          <CreditCard />
        </Fab>
      )}

      {/* Formulário de Novo Crédito */}
      <CreditoForm
        open={showCreditoForm}
        onClose={() => setShowCreditoForm(false)}
        onSuccess={handleCreditoSuccess}
        onError={handleCreditoError}
      />
    </Box>
  );
};

export default CreditosPage;
